/**
 * ReportFilterPanel Component
 * Filter panel for reports with department, team, and owner selection.
 *
 * Departments and teams are real ids (uuids) from `/reports/filter-options`; the
 * API narrows to OKRs whose owner is a live member of a picked department or team.
 *
 * Story 1.4: Report Filter Panel Component
 */

import React, { useState, useEffect } from 'react';
import { X, Filter, RotateCcw, Building2, Users, User } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Badge } from '../common';
import apiClient from '../../services/api';
import type { ReportFilterParams } from '../../services/reportApi';

// Filter types for reports
export interface ReportFilters {
  /** Department ids (uuids) — the API filters on the owner's department memberships. */
  departmentIds: string[];
  /** Team ids (uuids) — the API filters on the owner's team memberships. */
  teamIds: string[];
  ownerIds: string[];
}

// Filter option from API
interface FilterOption {
  id: string;
  name: string;
  count?: number;
}

// Metadata for available filter options
interface ReportFilterMetadata {
  departments: FilterOption[];
  teams: FilterOption[];
  owners: FilterOption[];
}

export interface ReportFilterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Current filter values */
  filters: ReportFilters;
  /** Callback when filters change */
  onFiltersChange: (filters: ReportFilters) => void;
  /** Callback when apply is clicked */
  onApply?: () => void;
}

// Default empty filters
export const DEFAULT_REPORT_FILTERS: ReportFilters = {
  departmentIds: [],
  teamIds: [],
  ownerIds: [],
};

/**
 * Check if filters are active (not default)
 */
export function hasActiveReportFilters(filters: ReportFilters): boolean {
  return (
    filters.departmentIds.length > 0 ||
    filters.teamIds.length > 0 ||
    filters.ownerIds.length > 0
  );
}

/**
 * The panel's filters as API parameters. An empty array means "no restriction" HERE, at the UI
 * boundary — never send one to the API: the backend reads an empty `ownerIds` as a filter that
 * matched nobody, i.e. zero rows, which is the correct reading there.
 */
export function toReportFilterParams(filters?: ReportFilters): ReportFilterParams {
  return {
    departmentIds: filters?.departmentIds.length ? filters.departmentIds : undefined,
    teamIds: filters?.teamIds.length ? filters.teamIds : undefined,
    ownerIds: filters?.ownerIds.length ? filters.ownerIds : undefined,
  };
}

/**
 * Count active filters
 */
export function countActiveReportFilters(filters: ReportFilters): number {
  return (
    filters.departmentIds.length +
    filters.teamIds.length +
    filters.ownerIds.length
  );
}

export const ReportFilterPanel: React.FC<ReportFilterPanelProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
}) => {
  const [metadata, setMetadata] = useState<ReportFilterMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch filter metadata on open
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch filter options from API
        const response = await apiClient.get<{
          success: boolean;
          data: ReportFilterMetadata;
        }>('/reports/filter-options');

        if (response.data.success) {
          setMetadata(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch report filter options:', err);
        setError('Failed to load filter options');
        // Set empty metadata as fallback
        setMetadata({
          departments: [],
          teams: [],
          owners: [],
        });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchMetadata();
    }
  }, [isOpen]);

  // Handle filter changes
  const handleFilterChange = (key: keyof ReportFilters, value: string[]) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  // Toggle a single value in an array filter
  const toggleFilterValue = (key: keyof ReportFilters, value: string) => {
    const current = filters[key];
    const newValue = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    handleFilterChange(key, newValue);
  };

  // Clear all filters
  const handleClear = () => {
    onFiltersChange(DEFAULT_REPORT_FILTERS);
  };

  // Apply filters and close
  const handleApply = () => {
    onApply?.();
    onClose();
  };

  if (!isOpen) return null;

  const activeCount = countActiveReportFilters(filters);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="border-b border-stone-200 bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-2 text-white">
                <Filter className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Report Filters</h2>
                {activeCount > 0 && (
                  <Badge variant="primary" className="ml-2">
                    {activeCount} active
                  </Badge>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition-colors"
                title="Close filters"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : (
              <>
                {/* Department Filter */}
                <FilterSection
                  title="Department"
                  icon={<Building2 className="w-4 h-4" />}
                  options={metadata?.departments || []}
                  selected={filters.departmentIds}
                  onToggle={(id) => toggleFilterValue('departmentIds', id)}
                  onClear={() => handleFilterChange('departmentIds', [])}
                />

                {/* Team Filter */}
                <FilterSection
                  title="Team"
                  icon={<Users className="w-4 h-4" />}
                  options={metadata?.teams || []}
                  selected={filters.teamIds}
                  onToggle={(id) => toggleFilterValue('teamIds', id)}
                  onClear={() => handleFilterChange('teamIds', [])}
                />

                {/* Owner Filter */}
                <FilterSection
                  title="Owner"
                  icon={<User className="w-4 h-4" />}
                  options={metadata?.owners || []}
                  selected={filters.ownerIds}
                  onToggle={(id) => toggleFilterValue('ownerIds', id)}
                  onClear={() => handleFilterChange('ownerIds', [])}
                />
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-stone-200 p-4 bg-stone-50">
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={handleClear}
                disabled={activeCount === 0}
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Clear All
              </Button>
              <Button
                variant="primary"
                onClick={handleApply}
                className="flex-1"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Filter section sub-component
interface FilterSectionProps {
  title: string;
  icon: React.ReactNode;
  options: FilterOption[];
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  icon,
  options,
  selected,
  onToggle,
  onClear,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter options by search term
  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-stone-700 font-medium">
          {icon}
          {title}
          {selected.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-bold rounded-full">
              {selected.length}
            </span>
          )}
        </div>
        <svg
          className={clsx(
            'w-4 h-4 text-stone-400 transition-transform',
            expanded && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {expanded && (
        <div className="p-4">
          {/* Search within options */}
          {options.length > 5 && (
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 mb-3 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          )}

          {/* Options list */}
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-stone-400 py-2">
              {searchTerm ? 'No matches found' : `No ${title.toLowerCase()} available`}
            </p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {filteredOptions.map((option) => (
                <label
                  key={option.id}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
                    selected.includes(option.id)
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-stone-50 text-stone-700'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => onToggle(option.id)}
                    className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-primary-500"
                  />
                  <span className="flex-1 text-sm truncate">{option.name}</span>
                  {option.count !== undefined && (
                    <span className="text-xs text-stone-400">{option.count}</span>
                  )}
                </label>
              ))}
            </div>
          )}

          {/* Clear section button */}
          {selected.length > 0 && (
            <button
              onClick={onClear}
              className="mt-2 text-xs text-stone-500 hover:text-primary-600 transition-colors"
            >
              Clear {title.toLowerCase()} filter
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Active filters display component
 */
export interface ActiveReportFiltersProps {
  filters: ReportFilters;
  metadata: ReportFilterMetadata | null;
  onRemoveFilter: (key: keyof ReportFilters, value: string) => void;
  onClearAll: () => void;
}

export const ActiveReportFilters: React.FC<ActiveReportFiltersProps> = ({
  filters,
  metadata,
  onRemoveFilter,
  onClearAll,
}) => {
  const getOptionName = (key: keyof ReportFilters, id: string): string => {
    if (!metadata) return id;

    const options =
      key === 'departmentIds'
        ? metadata.departments
        : key === 'teamIds'
        ? metadata.teams
        : metadata.owners;

    return options.find((opt) => opt.id === id)?.name || id;
  };

  const allFilters: Array<{ key: keyof ReportFilters; value: string; label: string }> = [
    ...filters.departmentIds.map((id) => ({
      key: 'departmentIds' as const,
      value: id,
      label: `Dept: ${getOptionName('departmentIds', id)}`,
    })),
    ...filters.teamIds.map((id) => ({
      key: 'teamIds' as const,
      value: id,
      label: `Team: ${getOptionName('teamIds', id)}`,
    })),
    ...filters.ownerIds.map((id) => ({
      key: 'ownerIds' as const,
      value: id,
      label: `Owner: ${getOptionName('ownerIds', id)}`,
    })),
  ];

  if (allFilters.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {allFilters.map(({ key, value, label }) => (
        <span
          key={`${key}-${value}`}
          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-md text-sm"
        >
          {label}
          <button
            onClick={() => onRemoveFilter(key, value)}
            className="hover:bg-primary-100 rounded p-0.5"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <button
        onClick={onClearAll}
        className="text-xs text-stone-500 hover:text-primary-600 transition-colors ml-2"
      >
        Clear all
      </button>
    </div>
  );
};

export default ReportFilterPanel;
